import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Subject, takeUntil } from "rxjs";
import { SharedModule } from "../../shared/shared.module";
import {
  Contact,
  ContactGroup,
  ContactReference,
  ExtensionContact,
  isManualContact,
  ManualContact,
} from "../interfaces/contact.interface";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  IContactGroupCreateData,
  IContactGroupCreateResult,
  IContactGroupEditDialogData,
  IContactGroupEditResult,
  IContactWidget,
  IContactWidgetPermissions,
} from "../interfaces/contact-widget.interface";
import {
  IAppDashboard,
  IPermissionsFlags,
  IPluginPermissions,
  IUser,
  ModelService,
  PluginInjectorWidget,
  PluginInjectorService,
} from "core-client/api";
import { ContactUtils } from "../utils/contact-utils";
import {
  ContactWidgetData,
  ContactWidgetService,
} from "../services/contact-widget.service";
import { ContactGroupCreateComponent } from "./contact-group-create/contact-group-create.component";
import { ContactGroupEditComponent } from "./contact-group-edit/contact-group-edit.component";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "lib-contact-widget-view",
  imports: [SharedModule],
  templateUrl: "./contact-widget-view.component.html",
  styleUrl: "./contact-widget-view.component.scss",
})
export class ContactWidgetViewComponent implements AfterViewInit, OnDestroy {
  // Services
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly modelService: ModelService = inject(ModelService);
  private readonly pluginInjectorService: PluginInjectorService = inject(
    PluginInjectorService,
  );
  private readonly ngZone: NgZone = inject(NgZone);
  protected readonly translate: TranslateService = inject(TranslateService);

  // Inject the ContactWidgetService
  private readonly contactWidgetService: ContactWidgetService =
    inject(ContactWidgetService);

  // Input properties
  @Input()
  data: PluginInjectorWidget.IWidget | undefined;

  @Input()
  isHeaderHovered: boolean = false;

  @ViewChild("contactWidgetContainer", { static: false })
  protected contactWidgetContainer!: ElementRef;

  // Helper ContactUtils
  protected readonly ContactUtils: typeof ContactUtils = ContactUtils;

  // Private properties
  private resizeObserver: ResizeObserver | undefined;
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();

  // Widget properties
  protected contactWidget: IContactWidget | undefined;
  protected widgetId: string | undefined;
  protected currDashboardId: string | undefined;

  // Widget permissions
  protected widgetPermissions: IContactWidgetPermissions = {
    widgetEnabled: false,
    widgetSettingsEditEnabled: false,
  };

  // Reactive state signals - derived from service
  protected selectedTabIndex: WritableSignal<number> = signal(0);
  protected searchTerm: WritableSignal<string> = signal("");

  // Service data signals
  protected widgetData: WritableSignal<ContactWidgetData | null> = signal(null);
  protected isSocketConnected: WritableSignal<boolean> = signal(false);

  // Computed signals for derived state
  protected contactGroups: Signal<ContactGroup[]> = computed(() => {
    return this.widgetData()?.contactGroups || [];
  });

  protected extensionContacts: Signal<ExtensionContact[]> = computed(() => {
    return this.widgetData()?.extensionContacts || [];
  });

  protected manualContacts: Signal<ManualContact[]> = computed(() => {
    return this.widgetData()?.manualContacts || [];
  });

  // Find the current user's extension contact
  protected currentUserContact: Signal<ExtensionContact | undefined> = computed(
    () => {
      const extensions = this.extensionContacts();
      return extensions.find((contact) => contact.isCurrentUser);
    },
  );

  // Check if current user can transfer calls
  protected canTransferCall: Signal<boolean> = computed(() => {
    return ContactUtils.canTransferCall(this.currentUserContact());
  });

  // Check if a contact can be a transfer target
  protected canTransferToContact(contact: Contact): boolean {
    return ContactUtils.canBeTransferTarget(contact, this.currentUserContact());
  }

  // Merge all contact sources - extensionContacts, manualContacts, and future sources
  protected allContacts: Signal<Contact[]> = computed(() => {
    const extensionContacts = this.extensionContacts();
    const manualContacts = this.manualContacts();

    // Merge all contact sources into one array
    // Future sources (like external contacts, LDAP contacts, etc.) can be added here
    const allSources: Contact[] = [
      ...extensionContacts,
      ...manualContacts,
      // ...futureContactSource1,
      // ...futureContactSource2,
    ];

    return allSources;
  });

  protected getFilteredContactsForGroup(groupId: number): Contact[] {
    const groups = this.contactGroups();
    const group = groups.find((g: ContactGroup) => g.groupId === groupId);
    if (!group) {
      return [];
    }

    const combinedContacts = this.allContacts();

    // Sort contact references by their order property
    const sortedContactRefs = ContactUtils.sortContactReferences(
      group.contacts,
    );

    // Create contacts in the specified order
    const orderedContacts: Contact[] = [];

    sortedContactRefs.forEach((contactRef: ContactReference) => {
      const contact = combinedContacts.find(
        (c: Contact) =>
          c.contactId === contactRef.contactId &&
          c.source === contactRef.source,
      );
      if (contact) {
        orderedContacts.push(contact);
      }
    });

    // Apply filters while preserving order
    let contacts = orderedContacts;

    const searchTerm = this.searchTerm().toLowerCase();
    if (searchTerm) {
      contacts = contacts.filter(
        (contact: Contact) =>
          contact.name?.toLowerCase().includes(searchTerm) ||
          ContactUtils.getDisplayPhoneNumber(contact).includes(searchTerm),
      );
    }

    return contacts;
  }

  ngAfterViewInit(): void {
    this.initAfterView().then();
  }

  ngOnDestroy(): void {
    // Cleanup widget in service
    if (this.widgetId) {
      this.contactWidgetService.cleanupWidget(this.widgetId);
    }

    // Complete the unsubscribed subject to clean up subscriptions
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();

    // Clean up ResizeObserver
    this.resizeObserver?.disconnect();
  }

  // Initialization methods
  private async initAfterView(): Promise<void> {
    if (!this.data?.appPluginWidget) {
      console.error(`[Contact Widget] Widget data not correctly loaded.`);
      return;
    }

    // Retrieve widget permission settings
    await this.getWidgetAccessRights(
      this.modelService._user.getUser(),
      this.pluginInjectorService,
      this.data,
    );

    this.contactWidget = this.data.appPluginWidget as IContactWidget;
    this.widgetId = this.contactWidget?._id;

    const user = this.modelService._user.getUser();
    if (!user?._id) {
      console.error(`[Contact Widget] User not found.`);
      return;
    }

    this.currDashboardId = user.active_dashboard;

    if (!this.widgetId || !this.currDashboardId) {
      console.error(
        `[Contact Widget] Required widget ID or dashboard ID missing.`,
      );
      return;
    }

    // Initialize widget in service
    this.contactWidgetService.initializeWidget(
      this.widgetId,
      this.currDashboardId,
    );

    // Load initial widget data from a database (manual contacts and groups)
    await this.loadInitialWidgetData();

    // Setup service subscriptions
    this.setupServiceSubscriptions();

    // Observe mouse hovering on widget header
    this.observeStyleChanges();
  }

  /**
   * Load initial widget data from database settings with automatic order migration
   * This includes manual contacts and contact groups, which should be available immediately
   */
  private async loadInitialWidgetData(): Promise<void> {
    if (!this.widgetId) {
      console.error(
        `[Contact Widget] Cannot load initial data - widget ID missing`,
      );
      return;
    }

    try {
      console.log(
        `[Contact Widget ${this.widgetId}] Loading initial widget data from database`,
      );

      const widgetData = this.contactWidgetService.getWidgetData(this.widgetId);
      if (!widgetData) {
        console.error(
          `[Contact Widget ${this.widgetId}] Widget data not found in service`,
        );
        return;
      }

      // Load manual contacts from settings if available
      await this.contactWidgetService.loadAllManualContactsFromUser(
        this.widgetId,
      );

      // Load contact groups from settings with automatic migration if available
      if (
        this.contactWidget?.settings?.contactGroups &&
        this.contactWidget.settings.contactGroups.length > 0
      ) {
        console.log(
          `[Contact Widget ${this.widgetId}] Loading ${this.contactWidget.settings.contactGroups.length} contact groups from settings`,
        );

        // Check if migration is needed and perform it automatically
        const migrationPerformed =
          await this.contactWidgetService.checkAndMigrateWidgetSettings(
            this.widgetId,
            this.contactWidget,
          );

        if (!migrationPerformed) {
          // No migration needed, just update the groups normally
          this.contactWidgetService.updateContactGroups(this.widgetId, [
            ...this.contactWidget.settings.contactGroups,
          ]);
        }
        // If migration was performed, the service already updated the groups
      } else {
        // Create a default group if none exist
        console.log(
          `[Contact Widget ${this.widgetId}] No contact groups found, creating default group`,
        );
        const defaultGroup: ContactGroup = {
          groupId: 1,
          name: "Neue Gruppe",
          contacts: [], // Empty array, no contacts to order yet
          created: new Date(),
        };
        this.contactWidgetService.updateContactGroups(this.widgetId, [
          defaultGroup,
        ]);

        // Save the default group to database
        if (this.contactWidget?.settings) {
          this.contactWidget.settings.contactGroups = [defaultGroup];
          await this.contactWidgetService.saveContactWidgetSettingsToDatabase(
            this.widgetId,
            this.contactWidget,
          );
        }
      }

      console.log(
        `[Contact Widget ${this.widgetId}] Initial widget data loaded successfully`,
      );
    } catch (error) {
      console.error(
        `[Contact Widget ${this.widgetId}] Error loading initial widget data:`,
        error,
      );
    }
  }

  private setupServiceSubscriptions(): void {
    if (!this.widgetId) {
      console.error(
        `[Contact Widget] Cannot setup service subscriptions - widget ID missing`,
      );
      return;
    }

    // Subscribe to connection state changes
    this.contactWidgetService
      .getConnectionState()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((state: "disconnected" | "connecting" | "connected") => {
        this.ngZone.run(() => {
          this.isSocketConnected.set(state === "connected");
          console.log(
            `[Contact Widget ${this.widgetId}] Socket connection state: ${state}`,
          );

          if (state === "connected") {
            this.onSocketConnected();
          } else if (state === "disconnected") {
            this.onSocketDisconnected();
          }
        });
      });

    // Subscribe to widget data changes
    this.contactWidgetService
      .getWidgetDataStream(this.widgetId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((data: ContactWidgetData) => {
        console.log(
          `[Contact Widget ${this.widgetId}] Received widget data update:`,
          data,
        );
        this.widgetData.set(data);
      });
  }

  private onSocketConnected(): void {
    if (!this.widgetId) {
      console.error(
        `[Contact Widget] Cannot load widget settings - widget ID missing`,
      );
      return;
    }

    // Load extension contacts inherit in contact groups
    this.contactWidgetService.loadExtensionContactFromSettings(this.widgetId);
  }

  private onSocketDisconnected(): void {
    console.log(`[Contact Widget ${this.widgetId}] Socket disconnected`);
  }

  // Search and filter methods
  protected onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm.set(target.value);
  }

  // Group management methods - simplified without save logic
  protected openNewGroupDialog(): void {
    if (!this.widgetId) {
      console.error(
        "[Contact Widget] Cannot open new group dialog - widget ID missing",
      );
      return;
    }

    // Use allContacts which merges all contact sources
    const combinedContacts = this.allContacts();

    const data = {
      widget: this.contactWidget,
      contacts: combinedContacts,
      existingGroups: this.contactGroups(),
    } as IContactGroupCreateData;

    const dialogRef = this.dialog.open(ContactGroupCreateComponent, {
      panelClass: ["astimax-contact-group-create-dialog", "adx-dialog-large"],
      disableClose: true,
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IContactGroupCreateResult | string) => {
        if (result && typeof result === "object" && result?.success) {
          // Group was successfully created, refresh the view
          this.refreshWidgetData();

          // Switch to the new group tab if we know its position
          const groups = this.contactGroups();
          if (groups.length > 0) {
            this.selectedTabIndex.set(groups.length - 1);
          }
        }
      });
  }

  protected editContactGroup($event: MouseEvent, group: ContactGroup): void {
    $event.stopPropagation(); // Prevent tab switch

    if (!this.widgetId) {
      console.error("[Contact Widget] Cannot edit group - widget ID missing");
      return;
    }

    // Use allContacts which merges all contact sources
    const combinedContacts = this.allContacts();

    const data = {
      widget: this.contactWidget,
      groupName: group.name,
      groupId: group.groupId,
      contacts: combinedContacts,
      manualContacts: this.manualContacts(),
      selectedContactIds: group.contacts,
      existingGroups: this.contactGroups(),
    } as IContactGroupEditDialogData;

    const dialogRef = this.dialog.open(ContactGroupEditComponent, {
      panelClass: ["astimax-contact-group-edit-dialog", "adx-dialog-large"],
      disableClose: true,
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (result: IContactGroupEditResult | string) => {
        if (typeof result === "object" && result?.success) {
          // Operation was successful, refresh the view
          this.refreshWidgetData();

          if (result.action === "delete") {
            // Group was deleted, adjust selected tab if necessary
            const currentTabIndex = this.selectedTabIndex();
            const groups = this.contactGroups();
            const newIndex = Math.max(
              0,
              Math.min(currentTabIndex, groups.length - 1),
            );
            this.selectedTabIndex.set(newIndex);
          }
        }
      });
  }

  // Helper method to refresh widget data from service
  private refreshWidgetData(): void {
    if (this.widgetId) {
      const widgetData = this.contactWidgetService.getWidgetData(this.widgetId);
      if (widgetData) {
        this.widgetData.set(widgetData);
      }
    }
  }

  protected onAvatarError(contact: Contact): void {
    if (!this.widgetId) {
      return;
    }

    // Handle avatar error through service
    if (isManualContact(contact)) {
      const currentManualContacts = this.manualContacts();
      const updatedContacts = currentManualContacts.map((c: ManualContact) =>
        c.contactId === contact.contactId ? { ...c, avatar: undefined } : c,
      );
      this.contactWidgetService.updateManualContacts(
        this.widgetId,
        updatedContacts,
        false,
      );
    }
    // Note: Extension contacts will be handled by the service automatically via socket updates
  }

  private observeStyleChanges(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.setWidgetHeaderOnHover(this.isHeaderHovered);
    });

    if (this.contactWidgetContainer?.nativeElement) {
      this.resizeObserver.observe(this.contactWidgetContainer?.nativeElement);
    }
  }

  private setWidgetHeaderOnHover(isHeaderHovered: boolean): void {
    if (this.contactWidgetContainer?.nativeElement) {
      this.contactWidgetContainer.nativeElement.style.marginTop =
        isHeaderHovered ? "35px" : "0";
    }
  }

  private async getWidgetAccessRights(
    user: IUser,
    pluginInjectorService: PluginInjectorService,
    widget: PluginInjectorWidget.IWidget,
  ): Promise<void> {
    const appDashboard = user.app_dashboards.find(
      (dashboard: IAppDashboard) => dashboard._id === user.active_dashboard,
    );

    if (appDashboard) {
      const pluginPermissions =
        await pluginInjectorService.permissionService.getPermissions(
          appDashboard,
          user,
        );

      const accessRights = pluginPermissions.find(
        (plugin_permission: IPluginPermissions) =>
          plugin_permission.plugin_id === widget?.pluginId,
      );

      if (accessRights?.widget_permissions) {
        const modulePermissions = accessRights.widget_permissions[
          widget.pluginModule.toLowerCase()
        ] as IPermissionsFlags;

        const widgetEnabled =
          widget?.widgetPermissions?.widgetEnabledPermission?.permissionRef;
        const widgetSettingsEnabled =
          widget?.widgetPermissions?.widgetBasePermissions
            ?.widgetSettingsEnabledPermission?.permissionRef;

        this.widgetPermissions = {
          widgetEnabled: widgetEnabled
            ? modulePermissions[widgetEnabled]
            : false,
          widgetSettingsEditEnabled: widgetSettingsEnabled
            ? modulePermissions[widgetSettingsEnabled]
            : false,
        };
      }
    }
  }

  // Determine available actions for a contact
  // Needs later to be changed for permission for actions
  getContactActions(contact: Contact): string[] {
    return ["call"];
  }

  // Event handlers - will be implemented later
  protected initiateCall(contact: Contact): void {
    if (!this.widgetId || !this.isSocketConnected()) {
      this.snackBar.open(
        this.translate.instant("ASTIMAX.CONTACT.WIDGET.FAILED_INITIAL_CALL"),
        "Dismiss",
        {
          duration: 3000,
          panelClass: ["error-snackbar"],
        },
      );
      return;
    }

    this.contactWidgetService.initiateCall(
      this.widgetId,
      contact.contactId,
      ContactUtils.getDisplayPhoneNumber(contact),
    );

    this.snackBar.open(
      `${this.translate.instant("ASTIMAX.CONTACT.WIDGET.INITIATING_CALL_TO")} ${contact.name}...`,
      "Dismiss",
      {
        duration: 3000,
      },
    );
  }

  // Initiate call transfer
  protected transferCall(contact: Contact): void {
    if (!this.widgetId || !this.isSocketConnected()) {
      this.snackBar.open(
        this.translate.instant("ASTIMAX.CONTACT.WIDGET.FAILED_TRANSFER_CALL"),
        "Dismiss",
        {
          duration: 3000,
          panelClass: ["error-snackbar"],
        },
      );
      return;
    }

    const currentUser = this.currentUserContact();
    if (!currentUser) {
      console.error("[Contact Widget] Current user contact not found");
      return;
    }

    // Get the call party information
    const callParty = ContactUtils.getFirstCallParty(currentUser);
    if (!callParty) {
      console.error("[Contact Widget] No call party found for transfer");
      return;
    }

    console.log(
      `[Contact Widget] Transferring call from ${callParty.name} to ${contact.name}`,
    );

    // Call the service to perform the transfer
    this.contactWidgetService.transferCall(
      this.widgetId,
      currentUser.contactId,
      contact.contactId,
      ContactUtils.getDisplayPhoneNumber(contact),
      callParty,
    );

    this.snackBar.open(
      `${this.translate.instant("ASTIMAX.CONTACT.WIDGET.TRANSFERRING_CALL_TO")} ${contact.name}...`,
      "Dismiss",
      {
        duration: 3000,
      },
    );
  }
}
